import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Users, User, Network } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from "react-i18next";

interface Employee {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  position?: string;
  department?: string;
  manager_id?: string | null;
  invitation_status: string;
  accepted_at: string | null;
  invited_at: string;
}

interface FullOrgChartDialogProps {
  employees: Employee[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectEmployee?: (employee: Employee) => void;
}

interface EmployeeNodeProps {
  employee: Employee;
  employees: Employee[];
  level: number;
  onSelect?: (employee: Employee) => void;
}

const EmployeeNode = ({ employee, employees, level, onSelect }: EmployeeNodeProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(level < 2);
  
  const directReports = employees.filter(e => e.manager_id === employee.id);
  const hasReports = directReports.length > 0;
  
  const displayName = employee.first_name && employee.last_name 
    ? `${employee.first_name} ${employee.last_name}`
    : employee.email || t("dashboard.employees.noName", "No name");
  
  const initials = employee.first_name && employee.last_name
    ? `${employee.first_name[0]}${employee.last_name[0]}`
    : "?";
  
  const isActive = employee.accepted_at !== null;

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        {/* Expand/Collapse button */}
        {hasReports ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 shrink-0"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <div className="w-7 h-7 flex items-center justify-center shrink-0">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        )}
        
        {/* Employee card */}
        <button
          onClick={() => onSelect?.(employee)}
          className={cn(
            "flex-1 flex items-center gap-3 p-2 rounded-lg border transition-all text-left",
            hasReports 
              ? "bg-primary/5 border-primary/20 hover:border-primary/40" 
              : "bg-card hover:bg-muted/50 border-border hover:border-primary/40",
            !isActive && "opacity-60"
          )}
        >
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarFallback className={cn(
              "text-xs font-medium",
              hasReports ? "bg-primary/20 text-primary" : "bg-muted"
            )}>
              {initials}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm truncate">{displayName}</span>
              {hasReports && (
                <Badge variant="secondary" className="text-xs shrink-0 h-5">
                  <Users className="h-3 w-3 mr-1" />
                  {directReports.length}
                </Badge>
              )}
            </div>
            {employee.position && (
              <p className="text-xs text-muted-foreground truncate">
                {employee.position}
              </p>
            )}
          </div>
          
          <Badge 
            variant={isActive ? "default" : "outline"}
            className={cn(
              "shrink-0 text-xs h-5",
              isActive && "bg-success text-success-foreground"
            )}
          >
            {isActive ? t("dashboard.employees.status.active", "Active") : t("dashboard.employees.status.pending", "Pending")}
          </Badge>
        </button>
      </div>
      
      {/* Direct reports */}
      {hasReports && isExpanded && (
        <div className="mt-1 ml-4 pl-4 border-l-2 border-muted space-y-1">
          {directReports.map((report) => (
            <EmployeeNode
              key={report.id}
              employee={report}
              employees={employees}
              level={level + 1}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const FullOrgChartDialog = ({
  employees,
  open,
  onOpenChange,
  onSelectEmployee,
}: FullOrgChartDialogProps) => {
  const { t } = useTranslation();
  
  // Find root employees
  const employeeIds = new Set(employees.map(e => e.id));
  const rootEmployees = employees.filter(
    e => !e.manager_id || !employeeIds.has(e.manager_id)
  );
  
  // Sort: managers first, then by name
  const sortedRoots = [...rootEmployees].sort((a, b) => {
    const aReports = employees.filter(e => e.manager_id === a.id).length;
    const bReports = employees.filter(e => e.manager_id === b.id).length;
    if (bReports !== aReports) return bReports - aReports;
    const aName = `${a.first_name} ${a.last_name}`;
    const bName = `${b.first_name} ${b.last_name}`;
    return aName.localeCompare(bName);
  });

  // Stats
  const managersCount = employees.filter(
    e => employees.some(emp => emp.manager_id === e.id)
  ).length;
  const activeCount = employees.filter(e => e.accepted_at !== null).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-4 sm:p-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Network className="h-5 w-5" />
            {t("dashboard.employees.orgChart.full", "Full Organization Chart")}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-3">
            <span>{employees.length} {t("dashboard.employees.orgChart.employees", "employees")}</span>
            <span>•</span>
            <span>{managersCount} {t("dashboard.employees.orgChart.managers", "managers")}</span>
            <span>•</span>
            <span>{activeCount} {t("dashboard.employees.orgChart.active", "active")}</span>
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 px-4 sm:px-6 pb-4 sm:pb-6">
          {employees.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Network className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t("dashboard.employees.orgChart.noEmployees", "No employees to display the organization chart")}</p>
            </div>
          ) : (
            <div className="space-y-1 pt-4">
              {sortedRoots.map((employee) => (
                <EmployeeNode
                  key={employee.id}
                  employee={employee}
                  employees={employees}
                  level={0}
                  onSelect={onSelectEmployee}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
